import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Chip,
  Divider,
  IconButton
} from '@mui/material';
import { Edit as EditIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';

const BrandDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: config } = useSelector((state) => state.config);
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBrandDetails = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`${config.API_BASE_URL}/api/brands/${id}`);
        setBrand(response.data);
      } catch (error) {
        console.error('Error fetching brand details:', error);
        setError('Failed to load brand details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBrandDetails();
    }
  }, [id, config.API_BASE_URL]);

  const handleEdit = () => {
    navigate(`/brands/edit/${id}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!brand) {
    return (
      <Box p={3}>
        <Typography>Brand not found</Typography>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box mb={3} display="flex" alignItems="center">
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">Brand Details</Typography>
        <Box flexGrow={1} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        >
          Edit Brand
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center"
              p={2}
              border="1px solid #ddd"
              borderRadius={1}
            >
              {brand.icon ? (
                <img 
                  src={brand.icon} 
                  alt={brand.name}
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/200?text=No+Image';
                  }}
                />
              ) : (
                <Box 
                  width={200} 
                  height={200} 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center"
                  bgcolor="#f5f5f5"
                >
                  <Typography color="textSecondary">No Image</Typography>
                </Box>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Box mb={3}>
              <Typography variant="h4" gutterBottom>
                {brand.name}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box mb={3}>
                <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                  Brand ID:
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {brand._id}
                </Typography>
              </Box>
              
              <Box mb={3}>
                <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                  Categories:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                  {brand.categories && brand.categories.length > 0 ? (
                    brand.categories.map((category) => (
                      <Chip 
                        key={category._id || category}
                        label={category.name || category}
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No categories assigned
                    </Typography>
                  )}
                </Box>
              </Box>
              
              <Box>
                <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                  Created At:
                </Typography>
                <Typography variant="body1">
                  {new Date(brand.createdAt).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default BrandDetails;
